import { WechatOutlined } from '@ant-design/icons';
import { AccordionItem } from '@mm/components';
import { ConversationsIcon } from '@mm/icons';
import {
  ContactsSession,
  SelectedContact,
} from '@mm/state/interfaces/contacts';
import { useActor } from '@xstate/react';
import classNames from 'classnames';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { ContactContext, EmptySelectedContact } from '../../../contactMachine';
import { LiveChatContext } from '../../../LiveChatContext';
import { getFormattedDateFromMoment } from '../../../shared';
import './Sessions.pcss';

type Props = {
  collapsedContactView?: boolean;
  contact?: SelectedContact;
  onSessionClick?: (
    session: ContactsSession,
    contact: SelectedContact,
    setSessionIdCallback: Function,
  ) => void;
};

const Sessions = (props: Props) => {
  const { collapsedContactView, contact, onSessionClick } = props;
  const [showAll, setShowAll] = useState(false);
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );
  const [lcMachineState] = useActor(lcService);

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const contactToUse = contact || selectedContact;
  const { sessions = [] } = contactToUse;

  const selectedContactMachine = contactToUse.remoteId
    ? lcMachineState.context.contactsRefs[contactToUse.remoteId]
    : undefined;

  const { selectedSessionId } =
    (selectedContactMachine?.state.context as ContactContext) || {};

  const className = classNames('live-chat--contact-sessions');

  const memoizedSessions = useMemo(() => {
    // take last 5 elements if not showAll, and show all array if showAll
    const numberForSlice = showAll ? 0 : -5;

    const sliceAdjustment = showAll
      ? 0
      : sessions.length > 5
      ? sessions.length - 5
      : 0;

    return sessions.slice(numberForSlice).map((session, idx) => {
      const time = getFormattedDateFromMoment(moment(session.createdAt));

      return (
        <div
          className={
            selectedSessionId === session.id
              ? 'live-chat--contact-sessions-item active'
              : 'live-chat--contact-sessions-item'
          }
          key={session.id}
          onClick={async () => {
            const setSessionId = async (onSuccess?: () => {}) => {
              if (
                selectedSessionId === undefined ||
                selectedSessionId !== session.id
              ) {
                selectedContactMachine?.send({
                  type: 'LC_CONTACT_UPDATE',
                  payload: {
                    selectedSessionId: session.id,
                    nextSessionId:
                      sessions.length - sliceAdjustment - idx > 1
                        ? sessions[sliceAdjustment + idx + 1].id
                        : undefined,
                    previousSessionId:
                      sliceAdjustment + idx > 0
                        ? sessions[sliceAdjustment + idx - 1].id
                        : undefined,
                    archivedMessages: [],
                  },
                });

                selectedContactMachine?.send({
                  type: 'LC_CONTACT_GET_CONVERSATION_SESSION_MESSAGES',
                  payload: {
                    sessionId: session.id,
                    addingOlder: false,
                  },
                });
              } else {
                selectedContactMachine?.send({
                  type: 'LC_CONTACT_UPDATE',
                  payload: {
                    archivedMessages: [],
                    selectedSessionId: undefined,
                    nextSessionId: undefined,
                    previousSessionId: undefined,
                  },
                });
              }
              if (onSuccess) {
                onSuccess();
              }
            };

            if (onSessionClick) {
              return onSessionClick(session, contactToUse!, setSessionId);
            }

            return setSessionId();
          }}
        >
          {time}
        </div>
      );
    });
  }, [sessions, showAll, selectedSessionId]);

  const memoizedCallback = useCallback(() => {
    setShowAll(showAll => !showAll);
  }, [sessions, showAll]);

  if (collapsedContactView) {
    return (
      <div className="live-chat--contact__header--collapsed live-chat--contact-section-header--collapsed">
        <WechatOutlined className="contact-details__header-icon" />
        <span className="live-chat--contact__content-number--collapsed">
          ({sessions.length})
        </span>
      </div>
    );
  }
  return (
    <div className={className}>
      <AccordionItem
        icon={<WechatOutlined className="contact-details__header-icon" />}
        title="Conversations"
        count={sessions.length}
        className="contact-details__accordion-item"
        headingClassName="contact-details__accordion-item-heading"
        childrenWrapperClassName="contact-details__children-wrapper"
      >
        {sessions.length > 5 && (
          <div
            className="live-chat--contact-section-header-label"
            onClick={memoizedCallback}
          >
            {showAll ? 'show less' : 'show all'}
          </div>
        )}
        {memoizedSessions}
      </AccordionItem>
    </div>
  );
};

const SessionsWrapper = (props: Props) => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v?.lcServiceRef?.current,
  );

  return lcService ? <Sessions {...props} /> : null;
};

export default SessionsWrapper;
